import { User, UserManager, UserSettings } from 'oidc-client';
import { AuthProvider } from 'ra-core';
import axios from '@/lib/axios'
import { stringify } from '@/utils/queryStringWrapper'
import { AccessTokenClaims, CastedUserManager, userManager } from './odUserManager'
import { userApiClient } from '@/lib/client'
import retrieveAxiosErr from '@/utils/retrieveAxiosErr';

export interface ActivateAutoLoginInput {
  type: 'ActivateAutoLogin';
  method: 'RegisterActivate' | 'InvitationActivate';
  username: string;
  password: string;
  vCode: string;
}

interface DeviceFlowInfo {
  usercode: string
}
export interface MfaLoginInput {
  type: 'MfaLogin';
  username: string;
  password: string;
  loginId: number;
  vCode: string;
  vCodeId: number;
  mfaWay: number;
  deviceFlow?: DeviceFlowInfo;
}

export interface CustomerIdPRedirectLoginInput {
  type: 'CustomerIdPRedirectLogin';
  username: string;
  code: string;
  ssoProtocol: 'oidc' | 'saml';
  deviceFlow?: DeviceFlowInfo;
}

export type LoginInput =
  | ActivateAutoLoginInput
  | MfaLoginInput
  | CustomerIdPRedirectLoginInput

export type OdAuthProvider = {
  [P in keyof AuthProvider as string extends P ? never : number extends P ? never : P]-?: AuthProvider[P]
}
& { userManager: UserManager }


// usually, authProvider should not be directly used in your component; you should use the hook methods that wraps this utility.
const authProvider: OdAuthProvider = {
    login: (input: LoginInput) => {
      switch (input.type) {
        case 'ActivateAutoLogin':
          return afterActivateAutoLogin(input);
        // case 'OnDataLocalAccountLogin':
          // login phase 1, we challenge user with password, then give him a verification code
          // we are not actually going to initial phase 1 here, but document it here instead
        case 'MfaLogin':
          // login phase 2, we challenge user with verifaction code distributed via sms/email, 
          // then exchange id_token/access_token with auth_code
          return mfaLogin(input);
        case 'CustomerIdPRedirectLogin':
          return ssoLogin(input);
        default:
          return Promise.resolve();
      }

    },
    logout: async (x: {stayAfterLogout?:boolean}) => {
      await userManager.removeUser()

      // Todo: when user login with his organization's SSO IdP, we should invoke SSO sign out endpoint
      // Todo: when user login with OnData local IdP, we should invoke OnData's sign out endpoint
      // eventually, after logout, user will be redirected to login page
      // if (!window.location.pathname.startsWith('/login')) {
      //   window.location.replace('/login')
      // }
      if (x?.stayAfterLogout) { return false }
      return '/login'
    },
    checkError: (error) => {
      const status = error.status || error.body?.status;
      if (status === 401) {
        return Promise.reject();
      }
      if (status === 403) {
        // return Promise.reject({ logoutUser: false, redirectTo: '', });
        return Promise.resolve();
      }
      // other error code (404, 500, etc): no need to log out
      return Promise.resolve();
    },
    checkAuth: async () => {
      const user = await userManager.getUser()
      const [success, failAndLogin] = [() => Promise.resolve(), () => Promise.reject({ redirectTo: '/login' })]
      
      if (!user) { return failAndLogin() } 
      else if(user.expired) { 
        return (userManager as CastedUserManager)._inspectRefreshedStatus().then(success, failAndLogin) 
      } 
      return success()
    },
    getPermissions: async (params: any): Promise<ReadonlyArray<string>> => {
      const casted = userManager as CastedUserManager
      const user = await userManager.getUser()
      if (!user || user.expired) { 
        return [] 
      }

      try
      {
        const jwtAt = casted._joseUtil.parseJwt<AccessTokenClaims>(user.access_token)
        
        return (!jwtAt || !jwtAt.payload.permissions) ? []
        : jwtAt.payload.permissions instanceof Array  ? jwtAt.payload.permissions
        : [jwtAt.payload.permissions]
      
      } catch (err) {
        return []
      }
    },
    getIdentity: () => userApiClient.apiUsersProfileGet().then(resp => ({
      ...resp.data,
      id: resp.data.userId || -1,
    })),
    userManager: userManager,
};

export default authProvider;


const afterActivateAutoLogin = async (input: ActivateAutoLoginInput) => {
  try
  {
    const url = await userManager.metadataService.getTokenEndpoint()
    
    const resp = await axios.post<UserSettings>(url || '', stringify({
      grant_type: 'password',
      username: input.username,
      password: input.password,
      vCode: input.vCode,
      loginType: input.method === 'RegisterActivate' ? 1 : 3 /* InvitationActivate */,
      client_id: 'ondata-portal',
    }), { headers: { Accept: 'application/json' } });
    
    await storeUser(resp.data)
  }
  catch (err) {
    const errMsg = retrieveAxiosErr(err)
    return Promise.reject(errMsg)
  }
}

const mfaLogin = async (input: MfaLoginInput) => {
  try
  {
    const url  = await userManager.metadataService.getTokenEndpoint()
    
    const resp = await axios.post<UserSettings>(url || '', stringify({
      grant_type: 'password',
      username: input.username,
      password: input.password,
      loginId: input.loginId,
      vCode: input.vCode,
      vCodeId: input.vCodeId,
      mfaWay: input.mfaWay,
      loginType: 0,
      client_id: 'ondata-portal',
    }), { headers: { Accept: 'application/json' } })
    
    await storeUser(resp.data)
  }
  catch (err) {
    const errMsg = retrieveAxiosErr(err)
    return Promise.reject(errMsg)
  }
}

const ssoLogin = async (input: CustomerIdPRedirectLoginInput) => {
  try
  {
    const url = await userManager.metadataService.getTokenEndpoint()
    
    const resp = await axios.post<UserSettings>(url || '', stringify({
      grant_type: 'password',
      username: input.username,
      password: input.username,
      vCode: input.code,
      loginType: input.ssoProtocol === 'saml' ? 4 : 5,
      client_id: 'ondata-portal',
    }), { headers: { Accept: 'application/json' } })
    
    await storeUser(resp.data)
  }
  catch (err) {
    const errMsg = retrieveAxiosErr(err)
    return Promise.reject(errMsg)
  }
}

async function storeUser(us: UserSettings) {
  const casted = userManager as CastedUserManager
  const jwt = casted._joseUtil.parseJwt(us.access_token)
  us.profile = {
    sub: jwt.payload.sub,
    auth_time: jwt.payload.auth_time,
    iss: jwt.payload.iss,
    exp: jwt.payload.exp,
    aud: jwt.payload.aud,
    iat: jwt.payload.iat,
    permissions: jwt.payload.permissions,
  }

  const user = new User(us)

  if (!us.expires_at && (us as any).expires_in) {
    const now = (Date.now() / 1000) ^ 0;
    user.expires_at = now + (us as any).expires_in
  }

  await userManager.storeUser(user)
  casted.events.load(user)
}

